import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ExternalLink, FileText, Loader2, MoreHorizontal, RefreshCw, X } from "lucide-react";
import api from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { RELEASE_PRODUCT_LABELS, formatDate, formatDateTime, formatDuration, incidentEntryText, incidentMoveLabel, postmortemComplete, POSTMORTEM_FIELDS, productErrorMessage } from "../lib/product";
import { IncidentStateBadge, SeverityBadge, StatusBadge } from "../components/product/ProductBits";
import { IncidentEditDialog, IncidentItemDialog, IncidentStatusDialog, IncidentTicketDialog, PostmortemDialog } from "../components/product/IncidentDialogs";
import TaskFormSheet from "../components/TaskFormSheet";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

function SideRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm py-1.5">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-right text-slate-900 min-w-0 break-words">{children}</span>
    </div>
  );
}

function Text({ title, value, testid }) {
  if (!value) return null;
  return (
    <Card className="p-5"><h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-700 mt-1 whitespace-pre-line break-words" data-testid={testid}>{value}</p></Card>
  );
}

export default function ProductIncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incident, setIncident] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [dialog, setDialog] = useState(null);
  const [move, setMove] = useState(null);
  const [removing, setRemoving] = useState("");
  const [note, setNote] = useState("");
  const [noting, setNoting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/product/incidents/${incidentId}`);
      setIncident(data);
      setPhase("ready");
    } catch (e) {
      setErrorText(e?.response?.status === 404 ? "Cet incident n'existe pas." : productErrorMessage(e, "Impossible de charger l'incident."));
      setPhase("error");
    }
  }, [incidentId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  const applied = (data, message) => {
    if (!data) { load(); return; }
    setIncident(data);
    setDialog(null);
    setMove(null);
    if (message) toast.success(message);
  };

  const remove = async (kind, ref) => {
    setRemoving(`${kind}:${ref}`);
    try {
      const path = { ticket: `tickets/${ref}`, item: "item", task: `actions/${ref}` }[kind];
      const { data } = await api.delete(`/product/incidents/${incident.id}/${path}`);
      applied(data, "Lien retiré");
    } catch (e) {
      toast.error(productErrorMessage(e, "Impossible de retirer ce lien."));
      load();
    } finally {
      setRemoving("");
    }
  };

  const addNote = async () => {
    if (!note.trim() || noting) return;
    setNoting(true);
    try {
      const { data } = await api.post(`/product/incidents/${incident.id}/entries`, { body: note.trim() });
      applied(data, "Note ajoutée");
      setNote("");
    } catch (e) {
      toast.error(productErrorMessage(e, "Impossible d'ajouter la note."));
    } finally {
      setNoting(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3" data-testid="incident-loading"><Skeleton className="h-28 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="incident-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/produit/incidents">Retour aux incidents</Link></Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </div>
      </Card>
    );
  }

  const caps = incident.capabilities || {};
  const closed = incident.state === "closed";
  const forward = incident.moves.filter((m) => !m.backward);
  const back = incident.moves.filter((m) => m.backward);
  const pm = incident.postmortem;
  const canTask = caps.manage && !closed && hasPermission(user, "tasks:create");

  return (
    <div className="space-y-4" data-testid="incident-detail">
      <Link to="/produit/incidents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Incidents de production</Link>

      <Card className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="text-xs text-slate-500 tabular-nums" data-testid="incident-number">{incident.number} · {RELEASE_PRODUCT_LABELS[incident.product]}</div>
            <h1 className="font-display font-bold text-xl text-slate-900 break-words" data-testid="incident-title-text">{incident.title}</h1>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <SeverityBadge severity={incident.severity} /><IncidentStateBadge state={incident.state} />
              {incident.postmortem_due && <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800" data-testid="incident-pm-due">Post-mortem en retard</span>}
            </div>
          </div>
          {((caps.manage && !closed) || (incident.state === "open" && hasPermission(user, "product:delete"))) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="incident-more"><MoreHorizontal size={18} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Actions sur l'incident">
                <DropdownMenuItem onClick={() => setDialog("edit")} data-testid="incident-action-edit">Modifier</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog("ticket")} data-testid="incident-action-ticket">Rattacher un ticket</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDialog("item")} data-testid="incident-action-item">Lier le retour produit qui le corrige</DropdownMenuItem>
                {canTask && <DropdownMenuItem onClick={() => setDialog("task")} data-testid="incident-action-task">Ajouter une action préventive</DropdownMenuItem>}
                {incident.state === "open" && hasPermission(user, "product:delete") && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="incident-action-delete">Supprimer l'incident…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {caps.manage && (forward.length > 0 || back.length > 0) && (
          <div className="flex flex-wrap gap-2" data-testid="incident-moves">
            {forward.map((m) => <Button key={m.state} onClick={() => setMove(m)} className="w-full sm:w-auto" data-testid={`incident-move-${m.state}`}>{incidentMoveLabel(m)}</Button>)}
            {back.map((m) => <Button key={m.state} variant="outline" onClick={() => setMove(m)} className="w-full sm:w-auto" data-testid={`incident-move-${m.state}`}>{incidentMoveLabel(m)}</Button>)}
          </div>
        )}
        {closed && (
          <p className="text-sm text-slate-600" data-testid="incident-closed-hint">
            Cet incident est clos. S'il revient, <Link to={`/produit/incidents/nouveau?reopens=${incident.id}`} className="font-medium text-jamora hover:underline">ouvrez-en un nouveau, lié à celui-ci</Link>.
          </p>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4 min-w-0">
          <Text title="Impact constaté" value={incident.impact} testid="incident-impact-text" />
          <Text title="Contournement" value={incident.workaround} testid="incident-workaround-text" />

          <Card className="p-5 space-y-3" data-testid="incident-timeline">
            <h2 className="text-sm font-semibold text-slate-900">Chronologie</h2>
            {caps.manage && !closed && (
              <div className="space-y-2">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} placeholder="Ajouter une note (ce qui a été fait, constaté, décidé)…" aria-label="Nouvelle note" data-testid="incident-note" />
                <div className="flex justify-end">
                  <Button size="sm" onClick={addNote} disabled={!note.trim() || noting} data-testid="incident-note-submit">
                    {noting && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ajouter la note
                  </Button>
                </div>
              </div>
            )}
            <ol className="space-y-3">
              {incident.entries.map((e) => (
                <li key={e.id} className="text-sm border-l-2 border-slate-200 pl-3" data-testid={`incident-entry-${e.kind}`}>
                  <p className={`${e.kind === "note" ? "text-slate-800" : "text-slate-600"} whitespace-pre-line break-words`}>{e.kind === "note" ? e.body : incidentEntryText(e)}</p>
                  {e.kind !== "note" && e.body && <p className="text-xs text-slate-500 whitespace-pre-line break-words">Motif : {e.body}</p>}
                  <p className="text-xs text-slate-500">{formatDateTime(e.occurred_at)}{e.author_name ? ` · ${e.author_name}` : ""}</p>
                </li>
              ))}
            </ol>
          </Card>

          <Card className="p-5 space-y-3" data-testid="incident-postmortem">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><FileText size={14} className="text-slate-400" />Post-mortem</h2>
              {caps.postmortem && <Button variant="ghost" size="sm" onClick={() => setDialog("postmortem")} data-testid="incident-postmortem-edit">{pm ? "Modifier" : "Rédiger"}</Button>}
            </div>
            {!pm && (
              <p className="text-sm text-slate-500">
                {incident.postmortem_required ? "Obligatoire pour clore un incident majeur ou critique" : "Facultatif pour un incident mineur"}
                {incident.state === "resolved" ? "." : " ; il se rédige une fois l'incident résolu."}
              </p>
            )}
            {pm && (
              <div className="space-y-3">
                {!postmortemComplete(pm) && <p className="text-xs text-amber-800" data-testid="incident-postmortem-incomplete">Incomplet : il manque une partie.</p>}
                {POSTMORTEM_FIELDS.map((f) => (
                  <div key={f.id}>
                    <h3 className="text-xs font-medium text-slate-500">{f.label}</h3>
                    <p className="text-sm text-slate-800 whitespace-pre-line break-words">{pm[f.id] || <span className="text-slate-400">Non renseigné</span>}</p>
                  </div>
                ))}
                {pm.by_name && <p className="text-xs text-slate-500">{pm.by_name} · {formatDateTime(pm.at)}</p>}
              </div>
            )}
          </Card>
        </div>

        <aside className="space-y-4 min-w-0">
          <Card className="p-5 divide-y divide-slate-100" data-testid="incident-summary">
            <SideRow label="Début">{formatDateTime(incident.started_at)}</SideRow>
            <SideRow label="Détecté">{formatDateTime(incident.detected_at)}</SideRow>
            <SideRow label="Résolu">{incident.resolved_at ? formatDateTime(incident.resolved_at) : <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Durée"><span data-testid="incident-duration">{formatDuration(incident.duration_minutes)}</span></SideRow>
            <SideRow label="Pharmacies touchées"><span data-testid="incident-pharmacies">{incident.impacted_pharmacies || <span className="text-slate-400">—</span>}</span></SideRow>
            <SideRow label="Responsable">{incident.responsible_name || <span className="text-slate-400">—</span>}</SideRow>
            <SideRow label="Ouvert">{incident.created_by_name} · {formatDate(incident.created_at)}</SideRow>
            {incident.reopens_incident_id && <SideRow label="Suite de"><Link to={`/produit/incidents/${incident.reopens_incident_id}`} className="text-jamora hover:underline">Incident précédent</Link></SideRow>}
          </Card>

          <Card className="p-5 space-y-2" data-testid="incident-tickets">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Tickets ({incident.tickets.length})</h2>
              {caps.manage && !closed && <Button variant="ghost" size="sm" onClick={() => setDialog("ticket")}>Ajouter</Button>}
            </div>
            {incident.tickets.length === 0 ? <p className="text-sm text-slate-500">Aucun ticket rattaché.</p> : (
              <ul className="space-y-1.5">
                {incident.tickets.map((t) => (
                  <li key={t.id} className="flex items-start justify-between gap-2" data-testid={`incident-ticket-${t.number}`}>
                    <Link to={`/support/tickets/${t.id}`} className="text-sm hover:underline min-w-0 break-words"><span className="text-xs text-slate-500 tabular-nums">{t.number}</span> {t.title}</Link>
                    {caps.manage && !closed && (
                      <button type="button" aria-label={`Détacher ${t.number}`} disabled={removing === `ticket:${t.id}`} onClick={() => remove("ticket", t.id)} className="text-slate-400 hover:text-slate-700 shrink-0 mt-0.5">
                        {removing === `ticket:${t.id}` ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5 space-y-2" data-testid="incident-item">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Retour produit lié</h2>
              {caps.manage && !closed && !incident.item && <Button variant="ghost" size="sm" onClick={() => setDialog("item")}>Lier</Button>}
            </div>
            {!incident.item ? <p className="text-sm text-slate-500">Aucun retour produit ne corrige cet incident.</p> : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <Link to={`/produit/${incident.item.id}`} className="text-sm hover:underline break-words"><span className="text-xs text-slate-500 tabular-nums">{incident.item.number}</span> {incident.item.title}</Link>
                  <div><StatusBadge status={incident.item.status} /></div>
                </div>
                {caps.manage && !closed && (
                  <button type="button" aria-label="Délier le retour produit" disabled={removing === "item:x"} onClick={() => remove("item", "x")} className="text-slate-400 hover:text-slate-700 shrink-0 mt-0.5">
                    {removing === "item:x" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                  </button>
                )}
              </div>
            )}
          </Card>

          <Card className="p-5 space-y-2" data-testid="incident-actions">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Actions préventives ({incident.tasks.length})</h2>
              {canTask && <Button variant="ghost" size="sm" onClick={() => setDialog("task")}>Ajouter</Button>}
            </div>
            {incident.tasks.length === 0 ? <p className="text-sm text-slate-500">Aucune action. Ce sont des tâches ordinaires, rattachées ici.</p> : (
              <ul className="space-y-1.5">
                {incident.tasks.map((t) => (
                  <li key={t.id} className="flex items-start justify-between gap-2" data-testid="incident-task">
                    {t.visible ? (
                      <Link to="/tasks" state={{ openTask: t.id }} className="text-sm hover:underline min-w-0 break-words inline-flex items-start gap-1">
                        <ExternalLink size={12} className="mt-1 shrink-0" aria-hidden="true" />{t.title}
                        <span className="text-xs text-slate-500">{t.due_date ? ` · ${formatDate(t.due_date)}` : ""}</span>
                      </Link>
                    ) : <span className="text-sm text-slate-500">Tâche que vous ne pouvez pas consulter</span>}
                    {caps.manage && !closed && (
                      <button type="button" aria-label="Retirer cette action" disabled={removing === `task:${t.id}`} onClick={() => remove("task", t.id)} className="text-slate-400 hover:text-slate-700 shrink-0 mt-0.5">
                        {removing === `task:${t.id}` ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </div>

      <IncidentEditDialog incident={incident} open={dialog === "edit"} onOpenChange={(o) => setDialog(o ? "edit" : null)} onDone={(d) => applied(d, "Incident modifié")} />
      <IncidentStatusDialog incident={incident} move={move} onOpenChange={(o) => { if (!o) setMove(null); }} onDone={(d) => applied(d, "État mis à jour")} />
      <PostmortemDialog incident={incident} open={dialog === "postmortem"} onOpenChange={(o) => setDialog(o ? "postmortem" : null)} onDone={(d) => applied(d, "Post-mortem enregistré")} />
      <IncidentTicketDialog incident={incident} open={dialog === "ticket"} onOpenChange={(o) => setDialog(o ? "ticket" : null)} onDone={(d) => applied(d, "Ticket rattaché")} />
      <IncidentItemDialog incident={incident} open={dialog === "item"} onOpenChange={(o) => setDialog(o ? "item" : null)} onDone={(d) => applied(d, "Retour produit lié")} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? incident : null} testId="delete-product" onClose={() => setDialog(null)}
        title={`Supprimer l'incident ${incident.number} ?`}
        description="Il a été ouvert par erreur : il va dans la corbeille et se restaure pendant 30 jours. Un incident sur lequel quelque chose a été écrit ou lié se résout et se clôt à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/product/incidents/${incident.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/produit/incidents");
          } catch (e) {
            toast.error(productErrorMessage(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
      {canTask && (
        <TaskFormSheet open={dialog === "task"} onOpenChange={(o) => setDialog(o ? "task" : null)} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false}
          directory={[]} initialValues={{ domain: "product", title: `Action préventive · ${incident.number}` }}
          onSaved={async (task) => {
            setDialog(null);
            try {
              const { data } = await api.post(`/product/incidents/${incident.id}/actions`, { task_id: task.id });
              applied(data, "Action préventive ajoutée");
            } catch (e) {
              toast.error(productErrorMessage(e, "La tâche est créée mais n'a pas pu être reliée à l'incident."));
              load();
            }
          }} />
      )}
    </div>
  );
}
